import { useState } from 'react';
import { Building2, Calendar, Link2, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { useOnboardingSetup } from '../hooks/useApi';
import { useAuthStore } from '../stores/authStore';

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [calendlyLink, setCalendlyLink] = useState('');
  const [error, setError] = useState('');
  const { updateWorkspace } = useAuthStore();
  const setupMutation = useOnboardingSetup();

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setError('Please enter your company or organization name.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleFinish = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');

    try {
      await setupMutation.mutateAsync({
        companyName: companyName.trim(),
        name: companyName.trim(),
        calendlyLink: calendlyLink.trim() || undefined,
      });

      updateWorkspace({
        companyName: companyName.trim(),
        name: companyName.trim(),
        calendlyLink: calendlyLink.trim() || undefined,
      });

      window.location.href = '/';
    } catch (err) {
      console.error('Onboarding error:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-blue-600" />

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-blue-600 rounded-2xl mb-4 shadow-lg shadow-primary-500/25">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome to iHireFast!</h1>
          <p className="mt-2 text-gray-500">Let's get you set up in 30 seconds.</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`w-2.5 h-2.5 rounded-full transition-colors ${step >= 1 ? 'bg-primary-600' : 'bg-gray-200'}`} />
          <div className={`w-2.5 h-2.5 rounded-full transition-colors ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
        </div>

        {/* Step 1: Company Name */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Your company name</h2>
                <p className="text-xs text-gray-500">This appears on your application forms and emails</p>
              </div>
            </div>

            <form onSubmit={handleStep1} className="space-y-5">
              <div>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Recruiting"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Continue <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Calendly */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Interview scheduling</h2>
                <p className="text-xs text-gray-500">Optional — you can add this later</p>
              </div>
            </div>

            <div className="my-5 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Connect your Calendly link so applicants can self-schedule interviews. Don't have one? Create a free account at{' '}
                <a href="https://calendly.com/signup" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                  calendly.com/signup
                </a>
              </p>
            </div>

            <form onSubmit={handleFinish} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Calendly Link
                </label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={calendlyLink}
                    onChange={(e) => setCalendlyLink(e.target.value)}
                    placeholder="https://calendly.com/your-name"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  />
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleFinish()}
                  disabled={setupMutation.isPending}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Skip for now
                </button>
                <button
                  type="submit"
                  disabled={setupMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60"
                >
                  {setupMutation.isPending ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Setting up...</>
                  ) : (
                    <><CheckCircle className="w-5 h-5" /> Finish Setup</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          You can change everything later in Settings.
        </p>
      </div>
    </div>
  );
}
